(() => {
  if (customElements.get('sahaf-favorite-button')) return;
  const key = 'the_sahaf_favorites_v1';
  const listeners = new Set();
  const notify = () => listeners.forEach(listener => listener());
  const subscribe = listener => { listeners.add(listener); return () => listeners.delete(listener); };
  const normalize = item => {
    if (!item || !/^\d+$/.test(String(item.id)) || typeof item.title !== 'string' || typeof item.url !== 'string') return null;
    try {
      const url = new URL(item.url, location.origin);
      if (url.origin !== location.origin || !/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?products\/[^/]+\/?$/i.test(url.pathname)) return null;
      let image = '';
      if (typeof item.image === 'string' && item.image) {
        const source = new URL(item.image, location.origin);
        if (source.protocol === 'https:' && (source.origin === location.origin || source.hostname === 'cdn.shopify.com')) image = source.href;
      }
      return {id: String(item.id), title: item.title.slice(0, 500), url: url.pathname, image};
    } catch { return null; }
  };
  const read = () => {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    let data;
    try { data = JSON.parse(raw); } catch { return []; }
    if (!data || data.version !== 1 || !Array.isArray(data.items)) return [];
    const seen = new Set();
    return data.items.slice(0, 500).map(normalize).filter(item => {
      if (!item || seen.has(item.id)) return false;
      seen.add(item.id);
      return true;
    });
  };
  const write = items => {
    localStorage.setItem(key, JSON.stringify({version: 1, items}));
    notify();
  };
  window.addEventListener('storage', event => { if (event.key === key || event.key === null) notify(); });
  class FavoriteButton extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      this.status = this.querySelector('[role="status"]');
      this.item = normalize({id: this.dataset.productId, title: this.dataset.productTitle, url: this.dataset.productUrl, image: this.dataset.productImage});
      if (!this.button || !this.item) return;
      this.abort = new AbortController();
      this.unsubscribe = subscribe(() => this.render());
      this.button.addEventListener('click', () => {
        try {
          const items = read();
          const exists = items.some(item => item.id === this.item.id);
          write(exists ? items.filter(item => item.id !== this.item.id) : [...items, this.item]);
          this.status.textContent = exists ? this.dataset.removed : this.dataset.added;
        } catch { this.status.textContent = this.dataset.error; }
      }, {signal: this.abort.signal});
      this.button.hidden = false;
      this.render();
    }
    render() {
      try {
        const saved = read().some(item => item.id === this.item.id);
        this.button.setAttribute('aria-pressed', String(saved));
        this.querySelector('[data-favorite-add]').hidden = saved;
        this.querySelector('[data-favorite-remove]').hidden = !saved;
      } catch { this.status.textContent = this.dataset.error; }
    }
    disconnectedCallback() { this.abort?.abort(); this.unsubscribe?.(); }
  }
  class FavoritesLink extends HTMLElement {
    connectedCallback() { this.unsubscribe = subscribe(() => this.render()); this.render(); }
    render() {
      const link = this.querySelector('a');
      const badge = this.querySelector('[data-favorites-count]');
      if (!link || !badge) return;
      try {
        const count = read().length;
        badge.textContent = String(count);
        badge.hidden = count === 0;
        link.setAttribute('aria-label', count ? this.dataset.countLabel.replace('__COUNT__', String(count)) : this.dataset.label);
      } catch { badge.hidden = true; }
    }
    disconnectedCallback() { this.unsubscribe?.(); }
  }
  class FavoritesPage extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('[data-favorites-list]');
      this.empty = this.querySelector('[data-favorites-empty]');
      this.status = this.querySelector('[role="status"]');
      if (!this.list || !this.empty) return;
      this.abort = new AbortController();
      this.unsubscribe = subscribe(() => this.render());
      this.list.addEventListener('click', event => {
        const button = event.target.closest('[data-remove-id]');
        if (!button || !this.list.contains(button)) return;
        try {
          const buttons = [...this.list.querySelectorAll('[data-remove-id]')];
          const index = buttons.indexOf(button);
          write(read().filter(item => item.id !== button.dataset.removeId));
          this.status.textContent = this.dataset.removed;
          const remaining = this.list.querySelectorAll('[data-remove-id]');
          const target = remaining[Math.min(index, remaining.length - 1)] || this.empty.querySelector('a');
          target?.focus();
        } catch { this.status.textContent = this.dataset.error; }
      }, {signal: this.abort.signal});
      this.render();
    }
    render() {
      let items;
      try { items = read(); } catch { this.status.textContent = this.dataset.error; return; }
      this.empty.hidden = items.length !== 0;
      this.list.hidden = items.length === 0;
      const fragment = document.createDocumentFragment();
      items.forEach(item => {
        const card = document.createElement('li');
        card.className = 'sahaf-favorites__card';
        const cover = document.createElement('a');
        cover.href = item.url;
        cover.className = 'sahaf-favorites__image';
        cover.tabIndex = -1;
        cover.setAttribute('aria-hidden', 'true');
        if (item.image) {
          const image = document.createElement('img');
          image.src = item.image;
          image.alt = item.title;
          image.width = 180;
          image.height = 240;
          image.loading = 'lazy';
          cover.append(image);
        }
        const heading = document.createElement('h2');
        const title = document.createElement('a');
        title.href = item.url;
        title.textContent = item.title;
        heading.append(title);
        const open = document.createElement('a');
        open.href = item.url;
        open.className = 'sb-button';
        open.textContent = this.dataset.openLabel;
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'sahaf-favorites__remove';
        remove.dataset.removeId = item.id;
        remove.textContent = this.dataset.removeLabel;
        remove.setAttribute('aria-label', `${this.dataset.removeLabel}: ${item.title}`);
        card.append(cover, heading, open, remove);
        fragment.append(card);
      });
      this.list.replaceChildren(fragment);
    }
    disconnectedCallback() { this.abort?.abort(); this.unsubscribe?.(); }
  }
  customElements.define('sahaf-favorite-button', FavoriteButton);
  customElements.define('sahaf-favorites-link', FavoritesLink);
  customElements.define('sahaf-favorites-page', FavoritesPage);
})();
